/** @format */

import {
	Avatar,
	Box,
	Card,
	Chip,
	Container,
	Divider,
	IconButton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { fonts } from "../../utility/types/constant";
import RefreshIcon from "@mui/icons-material/Refresh";
import PropTypes from "prop-types";
import Scrollbar from "../scrollbar/Scrollbar";
import { useEffect, useState } from "react";
// import { useNavigate } from "react-router-dom";
import { mockCircles } from "../../utility/mockCircles/circle";
import { applyFilter2, emptyRows } from "../../utility/utils/utils";
import CircleTableHead from "../circle-contribution-table/CircleTableHead";
// import CircleTableRow from "../circle-contribution-table/CircleTableRow";
import TableEmptyRows from "../circle-contribution-table/TableEmptyRows";
import TableNoData from "../circle-contribution-table/TableNoData";
import Label from "../label/Label";
import { useDispatch, useSelector } from "react-redux";
import { BootstrapDialog } from "../modal/notification-modal/styles";
import {
	selectCurrentCycleOfCircle,
	selectLoadMemberContributionStatus,
	selectMembersContributionByUnion,
} from "../../redux/selectors/contributionSelector";
import { AppDispatch } from "../../redux/store/store";
import { getMembersContributionStatusOfCircleCycle } from "../../redux/actions/contributionAction";
import { selectCircleData } from "../../redux/selectors/circleSelector";
import { selectCurrentUser } from "../../redux/selectors/userSelector";
import { useParams } from "react-router-dom";
import LoadingSpinner from "../loading-spinner/LoadingSpinner";
import { colors } from "../../utility/types/constant";
import moment from "moment-timezone";

interface CircleMemberTableProps {
	open: boolean;
	handleClose: () => void;
	circleId?: string;
}

const CircleMemberTable = (props: CircleMemberTableProps) => {
	const { open, handleClose } = props;

	const dispatch = useDispatch<AppDispatch>();
	const user = useSelector(selectCurrentUser);
	const circleData = useSelector(selectCircleData);
	const currentCycle = useSelector((state: any) =>
		selectCurrentCycleOfCircle(state, circleData?._id ?? ""),
	);
	const { circleId } = useParams();
	const isLoading = useSelector(selectLoadMemberContributionStatus);
	// Use the unionId-based selector to get the correct member contribution status for this specific circle
	const memberContributionStatus = useSelector((state: any) =>
		selectMembersContributionByUnion(state, circleData?._id ?? ""),
	);

	useEffect(() => {
		if (user?._id && circleId && circleData?._id && currentCycle?._id) {
			dispatch(
				getMembersContributionStatusOfCircleCycle({
					unionId: circleData._id,
					cycleId: currentCycle._id,
				}),
			);
		}
	}, [dispatch, circleData, user?._id, currentCycle?._id, circleId]);

	const handleRefresh = () => {
		if (circleData?._id && currentCycle?._id) {
			dispatch(
				getMembersContributionStatusOfCircleCycle({
					unionId: circleData._id,
					cycleId: currentCycle._id,
				}),
			);
		}
	};

	const memberStatuses = memberContributionStatus?.memberStatuses || [];
	// Use the cycle's timezone for today's date (to match how contributions are stored on backend)
	// Default to America/Chicago if not available
	const cycleTimezone = (currentCycle as any)?.timezone || "America/Chicago";
	const today = moment().tz(cycleTimezone).format("YYYY-MM-DD");

	const contributionDays = circleData?.configuration?.contributionDays || [];

	const todayDayName = moment().tz(cycleTimezone).format("dddd"); // "Monday", "Tuesday", etc.

	const isTodayContributionDay = contributionDays.includes(todayDayName);

	const rows = memberStatuses.map((row) => {
		const contributedDates = row.contributionStatus
			.filter((cs) => cs.contributed)
			.map((cs) => cs.date);
		const lastContributedDate =
			contributedDates.length > 0 ?
				contributedDates[contributedDates.length - 1]
				: "-";
		// Check if member has contributed today (using local timezone)
		const contributedToday = row.contributionStatus.some(
			(cs) => cs.date === today && cs.contributed,
		);
		// Also check if they have ANY contributions at all (for the "Contributed" column)
		// const hasContributed = contributedDates.length > 0;
		// const totalDays = row.contributionStatus.filter(
		// 	(cs) => cs.contributed,
		// ).length;

		let contributionDisplayStatus: "Yes" | "No" | "NA";

		if (!isTodayContributionDay) {
			contributionDisplayStatus = "NA";
		} else {
			contributionDisplayStatus = contributedToday ? "Yes" : "No";
		}

		// Format today's date for display
		const currentDateFormatted = moment()
			.tz(cycleTimezone)
			.format("MMM D, YYYY");

		return {
			...row,
			contributedToday,
			contributionDisplayStatus,
			currentDateFormatted,
			contributedDates,
			lastContributedDate,
		};
	});

	const [page, setPage] = useState(0);

	const [order, setOrder] = useState("asc");

	const [selected, setSelected] = useState<string[]>([]);

	const [orderBy, setOrderBy] = useState("name");

	const [filterName] = useState("");

	const [rowsPerPage, setRowsPerPage] = useState(5);

	const handleSort = (id: any) => {
		const isAsc = orderBy === id && order === "asc";

		if (id !== "") {
			setOrder(isAsc ? "desc" : "asc");
			setOrderBy(id);
		}
	};

	const handleSelectAllClick = (event: any) => {
		if (event.target.checked) {
			const newSelecteds = mockCircles.map((n: any) => n.name);
			setSelected(newSelecteds);
			return;
		}
		setSelected([]);
	};

	const handleChangePage = (event: any, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: any) => {
		setPage(0);
		setRowsPerPage(parseInt(event.target.value, 5));
	};
	const dataFiltered = applyFilter2({
		inputData: rows,
	});

	const notFound = !dataFiltered.length && !!filterName;

	const ROW_HEIGHT = 98;

	const initialHeight = Math.min(dataFiltered.length * ROW_HEIGHT, 600);

	const [tableHeight, setTableHeight] = useState(initialHeight);

	// updates table height when data filtered changes
	useEffect(() => {
		setTableHeight(Math.min(dataFiltered.length * ROW_HEIGHT, 600));
	}, [dataFiltered]);

	return (
		<BootstrapDialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
			{/* ── Header band ─────────────────────────────────────────────── */}
			<Box
				sx={{
					bgcolor: colors.primary,
					px: { xs: 2.5, sm: 4 },
					py: 2.5,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
				}}>
				<Stack direction="row" spacing={1.5} alignItems="center">
					<Box
						sx={{
							width: 40,
							height: 40,
							borderRadius: "50%",
							bgcolor: alpha(colors.mainSecondary, 0.15),
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							flexShrink: 0,
						}}>
						<PeopleAltIcon sx={{ color: colors.mainSecondary, fontSize: 22 }} />
					</Box>
					<Box>
						<Typography
							sx={{
								fontFamily: fonts.montserrat,
								fontWeight: 800,
								fontSize: { xs: "1rem", sm: "1.15rem" },
								color: "#fff",
								lineHeight: 1.2,
							}}>
							Members Contribution Status
						</Typography>
						<Typography
							sx={{
								fontFamily: fonts.montserrat,
								fontSize: "0.72rem",
								color: alpha("#fff", 0.6),
								mt: 0.3,
							}}>
							Contribution activity for the current cycle
						</Typography>
					</Box>
				</Stack>

				<Stack direction="row" spacing={0.5} alignItems="center">
					<Tooltip title="Refresh status">
						<IconButton
							onClick={handleRefresh}
							disabled={isLoading}
							sx={{ color: alpha("#fff", 0.7) }}>
							<RefreshIcon
								sx={{
									fontSize: 20,
									animation: isLoading ? "spin 1s linear infinite" : "none",
									"@keyframes spin": {
										"0%": { transform: "rotate(0deg)" },
										"100%": { transform: "rotate(360deg)" },
									},
								}}
							/>
						</IconButton>
					</Tooltip>
					<IconButton
						onClick={handleClose}
						sx={{ color: alpha("#fff", 0.7) }}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Stack>
			</Box>

			{isLoading ?
				<LoadingSpinner
					loading={isLoading}
					size={35}
					sx={{ mt: 5, mb: 5 }}
					color={colors.blue}
				/>
				: dataFiltered.length === 0 ?
					<Box
						sx={{
							px: 4,
							py: 6,
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: 1.5,
						}}>
						<PeopleAltIcon
							sx={{ fontSize: 48, color: alpha(colors.primary, 0.18) }}
						/>
						<Typography
							sx={{
								fontFamily: fonts.montserrat,
								fontWeight: 600,
								fontSize: "0.9rem",
								color: alpha(colors.primary, 0.45),
								textAlign: "center",
							}}>
							No active contribution status to view
						</Typography>
					</Box>
					: <>
						<Scrollbar sx={{ height: tableHeight }}>
							<TableContainer sx={{ overflow: "auto" }}>
								<Table sx={{ minWidth: 750 }}>
									{/* Custom styled header */}
									<TableHead>
										<TableRow sx={{ bgcolor: alpha(colors.primary, 0.04) }}>
											{["Member", "Name", "Contributed Today", "Last Contribution", "Amount"].map(
												(h) => (
													<TableCell
														key={h}
														sx={{
															fontFamily: fonts.montserrat,
															fontWeight: 700,
															fontSize: "0.72rem",
															color: colors.darkGrey,
															textTransform: "uppercase",
															letterSpacing: "0.06em",
															py: 1.5,
														}}>
														{h}
													</TableCell>
												),
											)}
										</TableRow>
									</TableHead>

									<TableBody>
										{dataFiltered
											.slice(
												page * rowsPerPage,
												page * rowsPerPage + rowsPerPage,
											)
											.map((row: any) => (
												<CircleMemberTableRow
													key={row.member._id}
													avatarUrl={row.member.profileImage ?? ""}
													memberName={`${row.member.firstName} ${row.member.lastName}`}
													memberContributed={row.contributionDisplayStatus}
													currentDate={row.currentDateFormatted}
													lastContributedDate={row.lastContributedDate}
													amount={circleData?.configuration?.contributionAmount}
												/>
											))}
										<TableEmptyRows
											height={68}
											emptyRows={emptyRows(
												page,
												rowsPerPage,
												dataFiltered.length,
											)}
										/>
										{notFound && <TableNoData query={filterName} />}
									</TableBody>
								</Table>
							</TableContainer>
						</Scrollbar>

						<Divider />
						<TablePagination
							page={page}
							component="div"
							count={dataFiltered.length}
							rowsPerPage={rowsPerPage}
							onPageChange={handleChangePage}
							rowsPerPageOptions={[5, 10, 25]}
							onRowsPerPageChange={handleChangeRowsPerPage}
							sx={{
								"& .MuiTablePagination-toolbar": {
									fontFamily: fonts.montserrat,
									fontSize: "0.8rem",
								},
							}}
						/>
					</>
			}
		</BootstrapDialog>
	);
};

export default CircleMemberTable;

interface CircleMemberTableRowProps {
	avatarUrl: string;
	memberName: string;
	memberContributed: string;
	currentDate: string;
	amount?: number;
	selected?: any;
	lastContributedDate?: string;
}

function CircleMemberTableRow(props: CircleMemberTableRowProps) {
	const {
		avatarUrl,
		memberName,
		memberContributed,
		currentDate,
		amount,
		selected,
		lastContributedDate,
	} = props;

	return (
		<TableRow
			hover
			tabIndex={-1}
			role="checkbox"
			selected={selected}
			sx={{
				"&:hover": { bgcolor: alpha(colors.primary, 0.03) },
			}}>
			{/* Avatar */}
			<TableCell sx={{ pl: 2.5, width: 60 }}>
				<Avatar
					alt={memberName}
					src={avatarUrl}
					sx={{ width: 36, height: 36, fontSize: "0.9rem" }}>
					{memberName.charAt(0)}
				</Avatar>
			</TableCell>

			{/* Name */}
			<TableCell>
				<Typography
					sx={{
						fontFamily: fonts.montserrat,
						fontWeight: 700,
						fontSize: "0.82rem",
						color: colors.primary,
					}}>
					{memberName}
				</Typography>
			</TableCell>

			{/* Contributed */}
			<TableCell>
				<Stack direction="column" spacing={0.4}>
					<Typography
						sx={{
							fontFamily: fonts.montserrat,
							fontSize: "0.68rem",
							color: alpha(colors.primary, 0.45),
						}}>
						{currentDate}
					</Typography>
					{memberContributed === "Yes" ?
						<Chip
							icon={<CheckCircleIcon sx={{ fontSize: "13px !important" }} />}
							label="Yes"
							size="small"
							sx={{
								bgcolor: alpha(colors.mainSecondary, 0.12),
								color: colors.mainSecondary,
								fontFamily: fonts.montserrat,
								fontWeight: 700,
								fontSize: "0.68rem",
								border: `1px solid ${alpha(colors.mainSecondary, 0.3)}`,
								width: "fit-content",
							}}
						/>
						: memberContributed === "No" ?
							<Chip
								icon={<CancelIcon sx={{ fontSize: "13px !important" }} />}
								label="No"
								size="small"
								sx={{
									bgcolor: alpha("#CA3433", 0.08),
									color: "#CA3433",
									fontFamily: fonts.montserrat,
									fontWeight: 700,
									fontSize: "0.68rem",
									border: `1px solid ${alpha("#CA3433", 0.25)}`,
									width: "fit-content",
								}}
							/>
							: <Chip
								icon={<RemoveCircleOutlineIcon sx={{ fontSize: "13px !important" }} />}
								label="N/A"
								size="small"
								sx={{
									bgcolor: alpha(colors.primary, 0.06),
									color: alpha(colors.primary, 0.5),
									fontFamily: fonts.montserrat,
									fontWeight: 600,
									fontSize: "0.68rem",
									width: "fit-content",
								}}
							/>
					}
				</Stack>
			</TableCell>

			{/* Last Contribution */}
			<TableCell>
				<Typography
					sx={{
						fontFamily: fonts.montserrat,
						fontSize: "0.8rem",
						color: colors.darkGrey,
					}}>
					{lastContributedDate && lastContributedDate !== "-" ?
						new Date(lastContributedDate + "T00:00:00Z").toLocaleDateString(
							"en-US",
							{
								month: "short",
								day: "numeric",
								year: "numeric",
								timeZone: "UTC",
							},
						)
						: "-"}
				</Typography>
			</TableCell>

			{/* Amount */}
			<TableCell>
				<Typography
					sx={{
						fontFamily: fonts.montserrat,
						fontWeight: 700,
						fontSize: "0.82rem",
						color: colors.primary,
					}}>
					${amount}
				</Typography>
			</TableCell>
		</TableRow>
	);
}

CircleMemberTableRow.propTypes = {
	avatarUrl: PropTypes.string,
	memberName: PropTypes.string,
	memberContributed: PropTypes.any,
	currentDate: PropTypes.string,
	amount: PropTypes.any,
	selected: PropTypes.any,
	open: PropTypes.bool,
	handleClose: PropTypes.func,
};
